/**
 * "Show in article": highlight a fact-check claim in the rendered
 * article and scroll it into view. DOM half of lib/claimLocator.js.
 *
 * The article lives in an element marked `data-claim-scope="article"`
 * (ArticlePage — the read view, or the rich-text editor while
 * editing).
 *
 * Highlighting uses the CSS Custom Highlight API (`CSS.highlights`),
 * which paints ranges WITHOUT touching the DOM. That matters here: the
 * body is React-rendered (and TipTap-rendered while editing), and
 * wrapping text in <mark> would fight both. Browsers without the API
 * (older Safari) fall back to selecting the first match, which the
 * browser paints natively. The colours are the `::highlight(...)`
 * rules in index.css.
 */
import { locate } from "@/lib/claimLocator";

export const HIGHLIGHT_NAME = "fact-check-claim";
const SCOPE_SELECTOR = '[data-claim-scope="article"]';

// Text inside these is never searched: controls and image captions.
const SKIP_SELECTOR =
  'button, input, textarea, select, script, style, [data-claim-skip], [data-testid="body-image-strip"]';

const BLOCK_TAGS = new Set([
  "P", "DIV", "LI", "UL", "OL", "H1", "H2", "H3", "H4", "H5", "H6",
  "BLOCKQUOTE", "BR", "FIGURE", "FIGCAPTION", "SECTION", "ARTICLE",
  "TABLE", "TR", "TD", "TH", "PRE", "HR",
]);

export function highlightSupported() {
  return typeof CSS !== "undefined" && !!CSS.highlights && typeof Highlight !== "undefined";
}

/** Text nodes + block separators under `root`, in document order. */
function collectSegments(root) {
  const segs = [];
  const walk = (node) => {
    if (node.nodeType === 3) {
      segs.push({ text: node.nodeValue || "", node });
      return;
    }
    if (node.nodeType !== 1) return;
    if (node.matches && node.matches(SKIP_SELECTOR)) return;
    const block = BLOCK_TAGS.has(node.tagName);
    if (block) segs.push({ sep: true });
    for (let c = node.firstChild; c; c = c.nextSibling) walk(c);
    if (block) segs.push({ sep: true });
  };
  walk(root);
  return segs;
}

export function clearClaimHighlight() {
  if (highlightSupported()) CSS.highlights.delete(HIGHLIGHT_NAME);
}

/**
 * Highlight every occurrence of the first candidate found and scroll
 * the first one to the middle of its scroll container.
 * Returns the number of places highlighted (0 = not found).
 */
export function highlightClaim(candidates, doc = document) {
  clearClaimHighlight();
  const root = doc.querySelector(SCOPE_SELECTOR);
  if (!root) return 0;
  const segs = collectSegments(root);
  const found = locate(segs, candidates);
  if (!found) return 0;

  const ranges = found.matches.map((m) => {
    const r = doc.createRange();
    r.setStart(segs[m.start[0]].node, m.start[1]);
    r.setEnd(segs[m.end[0]].node, m.end[1]);
    return r;
  });

  if (highlightSupported()) {
    CSS.highlights.set(HIGHLIGHT_NAME, new Highlight(...ranges));
  } else {
    const sel = doc.getSelection && doc.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(ranges[0]);
    }
  }

  const el = ranges[0].startContainer.parentElement;
  if (el && el.scrollIntoView) {
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }
  return ranges.length;
}